import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { track, EVENTS } from '../../services/analytics';

// "2026-11-30" -> "Nov 30, 2026", the app's date (#3547: the card showed the
// raw ISO string). A value it cannot read is shown as it came.
export function dayLabel(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    if (!m) return String(iso || '');
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The prompts she just named, as rows the student can carry into the Essay
 * Workshop (#2249, founder pick A from the 2026-09-16 mocks).
 *
 * Founder, 2026-09-16: "right out of the chat options are provided to use
 * these and generate starters for the 4 prompts to work on in the Essay
 * help." Before this the answer ended in the chat and the student had to
 * find the workshop, find the school, and find those four prompts among
 * eight.
 *
 * Every row comes from the server's own knowledge-base read, INCLUDING its
 * index: the workshop addresses a prompt by its position in that same
 * array, so a row built from the reply text could open the wrong essay and
 * generate starters for it. The picker only ever selects from what was
 * read; it cannot introduce a prompt.
 *
 * Everything is checked on arrival, so the common case is one tap, and
 * unchecking is how a student says "just these two for now".
 */
const shorten = (text, n = 132) => {
    const t = String(text || '').trim();
    return t.length > n ? `${t.slice(0, n - 1).trimEnd()}...` : t;
};

const wordLimitLabel = (wl) => {
    if (typeof wl === 'number' && wl > 0) return `${wl} words`;
    if (typeof wl === 'string' && wl.trim()) return wl.trim();
    return null;
};

export default function EssayPromptPicker({ picker, onNavigated }) {
    const rows = useMemo(
        () => (Array.isArray(picker?.prompts) ? picker.prompts : [])
            .filter((p) => p && typeof p.index === 'number' && typeof p.prompt === 'string'),
        [picker],
    );
    // Keyed by index, so a re-render of the same panel keeps the student's
    // unchecking; a NEW panel (different school or prompts) starts fresh.
    const [chosen, setChosen] = useState(() => new Set(rows.map((r) => r.index)));
    const navigate = useNavigate();

    if (!rows.length || !picker?.university_id) return null;

    const school = picker.university_name || 'this school';
    const toggle = (idx) => setChosen((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx); else next.add(idx);
        return next;
    });

    const picked = rows.filter((r) => chosen.has(r.index)).map((r) => r.index);
    const go = () => {
        if (!picked.length) return;
        track(EVENTS.ESSAY_PICKER_OPENED, { count: picked.length, offered: rows.length });
        onNavigated?.();
        navigate(`/essay-help/${picker.university_id}?prompts=${picked.join(',')}&from=astra`);
    };

    const deadline = picker.deadline?.date;
    return (
        <div data-testid="concierge-essay-picker"
             className="mt-2 rounded-xl border border-[#D6E8D5] bg-white p-3 shadow-sm">
            <p className="font-serif text-[15px] font-semibold text-[#1A4D2E]">
                Work on these in the Essay Workshop
            </p>
            <p className="mb-2.5 text-[11.5px] text-[#6B6B6B]">
                {picker.choose
                    ? `${school} asks for ${picker.choose}. Pick the ones you want and I will open each with three starters from your record.`
                    : `Pick the ones you want and I will open each with three starters from your record.`}
            </p>

            <ul className="m-0 list-none space-y-1.5 p-0">
                {rows.map((r) => {
                    const on = chosen.has(r.index);
                    const wl = wordLimitLabel(r.word_limit);
                    return (
                        <li key={r.index}>
                            <button
                                type="button"
                                onClick={() => toggle(r.index)}
                                aria-pressed={on}
                                data-testid={`essay-picker-row-${r.index}`}
                                className={`flex w-full items-start gap-2.5 rounded-lg border px-2.5 py-2 text-left
                                    ${on ? 'border-[#1A4D2E] bg-[#F7FBF7]' : 'border-[#EFEDE7] bg-white hover:bg-[#FAFAF7]'}`}
                            >
                                <span aria-hidden="true"
                                      className={`mt-0.5 flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded
                                          border text-[11px] font-bold text-white
                                          ${on ? 'border-[#1A4D2E] bg-[#1A4D2E]' : 'border-[#B9C4B9] bg-white'}`}>
                                    {on ? '✓' : ''}
                                </span>
                                <span className="min-w-0">
                                    <span className="flex flex-wrap items-baseline gap-x-2">
                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8A82]">
                                            Prompt {r.index + 1}
                                        </span>
                                        {wl && <span className="text-[11px] text-[#8A8A82]">{wl}</span>}
                                    </span>
                                    <span className="mt-0.5 block text-[12.5px] leading-snug text-[#2C2C2C]">
                                        {shorten(r.prompt)}
                                    </span>
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>

            <button
                type="button"
                onClick={go}
                disabled={!picked.length}
                data-testid="essay-picker-open"
                className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#1A4D2E]
                           px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-[#2D6B45]
                           disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
                <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                {picked.length === rows.length
                    ? `Open the Workshop with ${picked.length === 1 ? 'this one' : `these ${picked.length}`}`
                    : `Open the Workshop with ${picked.length} selected`}
            </button>
            {deadline && (
                <p className="mt-1.5 text-[11px] text-[#8A8A82]" data-testid="essay-picker-deadline">
                    {picker.deadline.plan_type ? `${picker.deadline.plan_type}: ` : 'Due '}
                    {dayLabel(deadline)}
                </p>
            )}
            <p className="mt-1.5 text-[11px] text-[#8A8A82]">
                Nothing is written for you. Starters are ways in, from your own record.
            </p>
        </div>
    );
}
